<template>
  <div class="nexus-container">
    <v-container fluid fill-height>
      <!-- HEADER -->
      <v-row class="pa-4 mb-2" align="center">
        <v-col cols="12">
          <div class="header-layout">
            <v-btn icon class="back-button-inline" @click="confirmBackNavigation">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            
            <h1 class="nexus-title">ALL RETURNS</h1>
            
            <v-btn
              v-if="!isTechnician && hasReturnEditPermission"
              class="nexus-btn"
              @click="navigateToAddReport"
            >
              <v-icon class="mr-2">mdi-plus-circle</v-icon>
              Add New Return
            </v-btn>
            <div v-else style="width: 180px;"></div>
          </div>
        </v-col>
      </v-row>

      <!-- LOADER -->
      <v-row v-if="loading" justify="center" align="center" class="loader-row">
        <v-progress-circular indeterminate color="white" size="64"></v-progress-circular>
      </v-row>

      <!-- MAIN CONTENT -->
      <v-row v-if="!loading" justify="center">
        <v-col cols="12" md="11">
          <v-card elevation="0" class="nexus-card">
            <v-card-text class="pa-6">
              <!-- SEARCH / FILTER -->
              <div class="redesign-section-header">
                <h2 class="redesign-section-title">SEARCH & FILTER</h2>
              </div>

              <v-row class="mt-4 mb-6">
                <v-col cols="12" md="6">
                  <div class="field-group">
                    <div class="field-label">
                      <v-icon size="small">mdi-magnify</v-icon>
                      Search Returns
                    </div>
                    <v-text-field
                      v-model="search"
                      outlined
                      dense
                      hide-details
                      class="nexus-input mt-2"
                      placeholder="Search by ID, customer, status..."
                    ></v-text-field>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="field-group">
                    <div class="field-label">
                      <v-icon size="small">mdi-filter</v-icon>
                      Filter by Status
                    </div>
                    <v-select
                      v-model="selectedStatus"
                      :items="statusOptionsWithClear"
                      outlined
                      dense
                      hide-details
                      class="nexus-input mt-2"
                      @update:model-value="handleStatusChange"
                    ></v-select>
                  </div>
                </v-col>
              </v-row>

              <!-- RETURN CARDS SECTION -->
              <div class="redesign-section-header mt-6">
                <h2 class="redesign-section-title">RETURNS ({{ filteredItems.length }})</h2>
              </div>

              <v-row class="mt-4">
                <v-col
                  v-for="(item, index) in paginatedItems"
                  :key="item.id || index"
                  cols="12" sm="6" md="4" lg="3"
                >
                  <div class="return-card" @click="openReturn(item)">
                    <!-- Card Header -->
                    <div class="return-card-header">
                      <v-chip
                        :color="getChipColor(item.status)"
                        size="small"
                        label
                      >
                        {{ item.status || 'Unknown' }}
                      </v-chip>
                      <div class="return-id">#{{ item.returnId || 'N/A' }}</div>
                    </div>

                    <!-- Card Body -->
                    <div class="return-card-body">
                      <div class="return-detail">
                        <span class="detail-icon">📅</span>
                        <div>
                          <div class="detail-label-small">Delivery Date</div>
                          <div class="detail-value">{{ formatDate(item.deliveryDate) }}</div>
                        </div>
                      </div>

                      <div class="return-detail">
                        <span class="detail-icon">🕐</span>
                        <div>
                          <div class="detail-label-small">Created Date</div>
                          <div class="detail-value">{{ formatDate(item.createdDate) }}</div>
                        </div>
                      </div>

                      <div class="return-detail">
                        <span class="detail-icon">👤</span>
                        <div>
                          <div class="detail-label-small">Received By</div>
                          <div class="detail-value">{{ item.receivedBy || 'N/A' }}</div>
                        </div>
                      </div>

                      <div class="return-detail">
                        <span class="detail-icon">🏢</span>
                        <div>
                          <div class="detail-label-small">Customer</div>
                          <div class="detail-value">{{ item.customerName || 'N/A' }}</div>
                        </div>
                      </div>
                    </div>

                    <!-- Card Footer -->
                    <div class="return-card-footer">
                      <v-btn
                        size="small"
                        variant="text"
                        @click.stop="openExtraInfo(item)"
                        class="info-btn"
                      >
                        <v-icon size="small">mdi-information</v-icon>
                        Info
                      </v-btn>
                      <v-btn
                        v-if="hasReturnEditPermission"
                        size="small"
                        class="nexus-btn"
                        @click.stop="openReturn(item)"
                      >
                        Open
                        <v-icon size="small" class="ml-1">mdi-arrow-right</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <!-- LOAD MORE -->
              <v-row justify="center" class="mt-6" v-if="canLoadMore">
                <v-btn class="nexus-btn-secondary" @click="loadMore">
                  <v-icon class="mr-2">mdi-plus</v-icon>
                  Load More
                </v-btn>
              </v-row>

              <!-- No Results -->
              <v-row v-if="filteredItems.length === 0" justify="center" class="mt-6">
                <div class="no-results">
                  <v-icon size="64" color="#94a3b8">mdi-file-search-outline</v-icon>
                  <p class="mt-4">No returns found matching your criteria</p>
                </div>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- BACK CONFIRMATION DIALOG -->
      <v-dialog v-model="backDialog" max-width="400px">
        <v-card>
          <v-card-title class="headline">Confirm Navigation</v-card-title>
          <v-card-text>Are you sure you want to go back?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="red-darken-1" variant="text" @click="backDialog = false">Cancel</v-btn>
            <v-btn color="green-darken-1" variant="text" @click="goBack">Yes, Go Back</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- EXTRA INFO DIALOG -->
      <v-dialog v-model="extraInfoDialog.visible" max-width="500px">
        <v-card class="info-dialog-card">
          <v-card-title class="info-dialog-header">
            <span>Return Preview</span>
            <v-spacer></v-spacer>
            <v-btn icon size="small" variant="text" @click="copyCardContent(extraInfoDialog.item)">
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
            <v-btn icon size="small" variant="text" @click="extraInfoDialog.visible = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pa-4">
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Return #</div>
                <div class="info-value">{{ extraInfoDialog.item.returnId }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Status</div>
                <v-chip :color="getChipColor(extraInfoDialog.item.status)" size="small" label>
                  {{ extraInfoDialog.item.status }}
                </v-chip>
              </div>
              <div class="info-item">
                <div class="info-label">Delivery Date</div>
                <div class="info-value">{{ formatDate(extraInfoDialog.item.deliveryDate) }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Created Date</div>
                <div class="info-value">{{ formatDate(extraInfoDialog.item.createdDate) }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Received By</div>
                <div class="info-value">{{ extraInfoDialog.item.receivedBy }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Assigned To</div>
                <div class="info-value">{{ extraInfoDialog.item.assignedTo?.name || 'N/A' }}</div>
              </div>
              <div class="info-item full-width">
                <div class="info-label">Customer</div>
                <div class="info-value">{{ extraInfoDialog.item.customerName }}</div>
              </div>
              <div class="info-item full-width" v-if="extraInfoDialog.item.description">
                <div class="info-label">Reason</div>
                <div class="info-value">{{ extraInfoDialog.item.description }}</div>
              </div>
              <div class="info-item full-width" v-if="extraInfoDialog.item.referenceNumbers && extraInfoDialog.item.referenceNumbers.length">
                <div class="info-label">Reference Numbers</div>
                <div class="info-value">{{ extraInfoDialog.item.referenceNumbers.join(', ') }}</div>
              </div>
            </div>
          </v-card-text>

          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn class="nexus-btn" @click="openReturn(extraInfoDialog.item)">
              <v-icon class="mr-1" size="small">mdi-open-in-new</v-icon>
              Open Return
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- SNACKBAR -->
      <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
        {{ snackbarMessage }}
        <template v-slot:actions>
          <v-btn color="white" variant="text" @click="snackbar = false">Close</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import {
  getFirestore, collection, getDocs, doc, getDoc
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "../../firebaseConfig";

const db   = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);

export default {
  name: "ReturnsComponent",
  data() {
    return {
      userRole: "",
      userPermissions: {},
      loading: true,

      /* list data */
      items: [],
      search: "",
      selectedStatus: "",
      statusOptions: [
        "Booked",
        "Inspection",
        "Pending Credit",
        "Labels and Packaging",
        "Return to Stores",
        "Completed",
        "Rejected",
      ],

      /* pagination */
      visibleCount: 12,
      loadIncrement: 12,

      /* ui states */
      backDialog: false,
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",

      /* extra-info dialog */
      extraInfoDialog: {
        visible: false,
        item: {}
      }
    };
  },

  computed: {
    statusOptionsWithClear() {
      return ["Clear Filter", ...this.statusOptions];
    },
    isTechnician() { return this.userRole === "Technician"; },
    hasReturnEditPermission() {
      return this.userPermissions.Add_Button || this.userPermissions.Edit_Details;
    },

    /* search + filter + sort */
    filteredItems() {
      let arr = this.items;

      if (this.search) {
        const s = this.search.toLowerCase();
        arr = arr.filter(i =>
          String(i.returnId).toLowerCase().includes(s) ||
          (i.customerName   && i.customerName.toLowerCase().includes(s)) ||
          (i.invoiceNumber  && i.invoiceNumber.toLowerCase().includes(s)) ||
          (i.description    && i.description.toLowerCase().includes(s)) ||
          (i.referenceNumbers && i.referenceNumbers.some(r => r.toLowerCase().includes(s))) ||
          (i.receivedBy     && i.receivedBy.toLowerCase().includes(s)) ||
          (i.assignedTo?.name && i.assignedTo.name.toLowerCase().includes(s))
        );
      }

      if (this.selectedStatus && this.selectedStatus !== "Clear Filter") {
        arr = arr.filter(i => i.status === this.selectedStatus);
      }

      return arr.sort((a, b) => (+b.returnId || 0) - (+a.returnId || 0));
    },

    paginatedItems() { return this.filteredItems.slice(0, this.visibleCount); },
    canLoadMore()      { return this.visibleCount < this.filteredItems.length; }
  },

  watch: {
    search()          { this.resetPagination(); },
    selectedStatus()  { this.resetPagination(); }
  },

 async created() {
  await this.fetchUserRole();
  await this.fetchReports();
  this.loading = false;
},
methods: {
  /* USER / PERMISSIONS */
  async fetchUserRole() {
    try {
      const user = auth.currentUser;
      if (!user) {
        this.userRole = "Guest";
        this.userPermissions = {};
        return;
      }
      const snap = await getDoc(doc(db, "users", user.uid));
      if (!snap.exists()) {
        this.userRole = "Guest";
        this.userPermissions = {};
        return;
      }
      const data = snap.data();
      this.userRole = data.role || "";
      this.userPermissions = data.returns || {};
    } catch (e) {
      console.error(e);
      this.userRole = "Guest";
      this.userPermissions = {};
      this.showSnackbar("Failed to load user permissions", "error");
    }
  },

    /* FETCH RETURNS */
    async fetchReports() {
      try {
        const snap = await getDocs(collection(db, "returns"));
        const list = [];

        snap.forEach(d => {
          const dt = d.data();
          list.push({
            id: d.id,
            returnId:      dt.returnId          ?? "N/A",
            invoiceNumber: dt.invoiceNumber     ?? "N/A",
            description:   dt.returnReason      ?? "N/A",
            status:        dt.returnStatus      ?? "Pending",
            referenceNumbers: dt.returnItems
              ? dt.returnItems.map(i => i.refnum || "").filter(Boolean)
              : [],
            deliveryDate:  dt.deliveryDate,
            createdDate:   dt.createdDate,
            receivedBy:    dt.receivedBy        ?? "N/A",
            assignedTo:    typeof dt.assignedTo === "object" ? dt.assignedTo : { name: "N/A" },
            customerName:  dt.customer?.companyName ?? "N/A"
          });
        });

        this.items = list.sort((a, b) => (+b.returnId || 0) - (+a.returnId || 0));
      } catch (e) {
        console.error("Returns fetch error:", e);
        this.showSnackbar("Failed to fetch returns", "error");
      }
    },

    /* NAVIGATION */
    openReturn(item) {
      window.location.href = `/EditReturns?id=${item.id}`;
    },
    navigateToAddReport()     { this.$router.push({ name: "AddReturns" }); },
    confirmBackNavigation()   { this.backDialog = true; },
    goBack()                  { this.backDialog = false; this.$router.go(-1); },

    /* EXTRA INFO DIALOG */
    openExtraInfo(item) {
      this.extraInfoDialog.item    = item;
      this.extraInfoDialog.visible = true;
    },

    /* AUX */
    handleStatusChange() {
      if (this.selectedStatus === "Clear Filter") this.clearFilters();
    },
    clearFilters()      { this.search = ""; this.selectedStatus = ""; },
    loadMore()          { this.visibleCount += this.loadIncrement; },
    resetPagination()   { this.visibleCount = this.loadIncrement; },

    getChipColor(s) {
      const map = {
        booked: "blue",
        inspection: "orange",
        "pending credit": "teal",
        "labels and packaging": "pink",
        "return to stores": "brown",
        completed: "green",
        rejected: "red"
      };
      return map[s?.toLowerCase()] || "grey";
    },

    formatDate(s) {
      const d = new Date(s);
      return s && !isNaN(d) ? d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "N/A";
    },

    showSnackbar(msg, color = "success") {
      this.snackbarMessage = msg;
      this.snackbarColor   = color;
      this.snackbar        = true;
    },

    copyCardContent(item) {
      const txt = `Return ID: ${item.returnId}
Status: ${item.status}
Delivery Date: ${this.formatDate(item.deliveryDate)}
Created Date: ${this.formatDate(item.createdDate)}
Received By: ${item.receivedBy}
Assigned To: ${item.assignedTo?.name}
Customer: ${item.customerName}`;
      navigator.clipboard.writeText(txt)
        .then(() => this.showSnackbar("Copied!"))
        .catch(() => this.showSnackbar("Copy failed", "error"));
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");

/* ========================================
   NEXUS CONTAINER & BASE STYLES
   ======================================== */

.nexus-container {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  min-height: 100vh;
  padding: 20px;
  font-family: 'Inter', sans-serif;
}

.nexus-title {
  color: white;
  font-size: 2.5rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
}

/* ========================================
   HEADER LAYOUT
   ======================================== */

.header-layout {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.back-button-inline {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.back-button-inline:hover {
  background: rgba(255, 255, 255, 0.3) !important;
  transform: scale(1.05);
}

.back-button-inline .v-icon {
  color: white !important;
}

.nexus-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  margin-top: 20px;
}

/* ========================================
   SECTION HEADERS
   ======================================== */

.redesign-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e2e8f0;
}

.redesign-section-title {
  color: #1e293b;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

/* ========================================
   FIELD GROUPS
   ======================================== */

.field-group {
  background: white;
  border-radius: 10px;
  padding: 14px;
  border: 2px solid #e2e8f0;
}

.field-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 500;
  margin-bottom: 0;
}

/* ========================================
   INPUT STYLING
   ======================================== */

.nexus-input :deep(.v-input__control) {
  border-radius: 8px;
}

.nexus-input :deep(.v-field) {
  background: rgba(248, 250, 252, 0.8) !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
}

.nexus-input :deep(.v-field:hover) {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 0.9) !important;
}

.nexus-input :deep(.v-field--focused) {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 0 0 3px rgba(71, 85, 105, 0.15);
}

/* ========================================
   RETURN CARDS
   ======================================== */

.return-card {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.return-card:hover {
  border-color: #475569;
  box-shadow: 0 8px 25px rgba(71, 85, 105, 0.15);
  transform: translateY(-4px);
}

.return-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.return-id {
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
}

.return-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.return-detail {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.detail-icon {
  font-size: 1rem;
  width: 24px;
  text-align: center;
}

.detail-label-small {
  font-size: 0.65rem;
  color: #94a3b8;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 0.8rem;
  color: #334155;
  font-weight: 500;
}

.return-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.info-btn {
  color: #475569 !important;
  font-size: 0.75rem !important;
}

/* ========================================
   BUTTONS
   ======================================== */

.nexus-btn {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
  color: white !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  padding: 10px 20px !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.3) !important;
  transition: all 0.3s ease !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.nexus-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px 0 rgba(71, 85, 105, 0.4) !important;
}

.nexus-btn-secondary {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%) !important;
  color: white !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  padding: 12px 24px !important;
  box-shadow: 0 2px 8px rgba(100, 116, 139, 0.3) !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.nexus-btn-secondary:hover {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(100, 116, 139, 0.4) !important;
}

/* Back button now uses .back-button-inline in header */

/* ========================================
   INFO DIALOG
   ======================================== */

.info-dialog-card {
  border-radius: 16px !important;
}

.info-dialog-header {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  color: white;
  padding: 16px 20px;
}

.info-dialog-header .v-icon {
  color: white !important;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.info-item {
  padding: 10px;
  background: #f8fafc;
  border-radius: 8px;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-label {
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.info-value {
  font-size: 0.9rem;
  color: #1e293b;
  font-weight: 500;
}

/* ========================================
   NO RESULTS
   ======================================== */

.no-results {
  text-align: center;
  padding: 40px;
  color: #64748b;
}

/* ========================================
   LOADER
   ======================================== */

.loader-row {
  margin-top: 100px;
}

/* ========================================
   RESPONSIVE DESIGN
   ======================================== */

@media (max-width: 768px) {
  .nexus-title {
    font-size: 1.5rem;
  }

  .header-layout {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }

  .back-button-inline {
    position: absolute;
    top: 20px;
    left: 16px;
  }

  .redesign-section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .return-card {
    margin-bottom: 16px;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .nexus-card {
    margin: 10px;
    border-radius: 16px;
  }
}

/* ========================================
   CUSTOM SCROLLBAR
   ======================================== */

::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
}
</style>